import React, { FunctionComponent, useEffect, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import AddInvestmentDocuments from './add-investment-documents';
import {
  AdminInvestmentDocument,
  AdminProductDistributionPlatformListing,
  DistributionPlatformPermissionEnum,
} from 'api';
import { useCurrentUserSelector } from 'core/auth/hooks';
import { hasDistributionViewInformationPermission } from 'src/apps/issuer/helpers/distributionPlatformPermissions';
import InvestmentDocumentsList from './investment-documents-list';

interface InvestmentDocumentsProps {
  listings: AdminProductDistributionPlatformListing[];
  onInvestmentDocumentsAdded: () => void;
}

const InvestmentDocuments: FunctionComponent<InvestmentDocumentsProps> = ({ listings, onInvestmentDocumentsAdded }) => {
  const [hasViewDistributionInformationPermission, setHasViewDistributionInformationPermission] = useState(false);
  const [listingsWithEditPermission, setListingsWithEditPermission] = useState<
    AdminProductDistributionPlatformListing[]
  >([]);
  const [investmentDocuments, setInvestmentDocuments] = useState<
    (AdminInvestmentDocument & { distributionPlatformName: string; distributionPlatformId: string })[]
  >([]);

  const { currentUser } = useCurrentUserSelector();

  useEffect(() => {
    // check view permission only for the platforms that have been listed
    const relevantDistributionPlatforms = (currentUser?.associatedDistributionPlatforms || []).filter(
      (platform) =>
        hasDistributionViewInformationPermission([platform]) &&
        listings.some((listing) => listing.usesEffecta === true && listing.distributionPlatformId === platform.id),
    );

    const hasViewPermission = hasDistributionViewInformationPermission(relevantDistributionPlatforms);
    setHasViewDistributionInformationPermission(hasViewPermission);

    if (!hasViewPermission) return;

    // get investment documents
    const docs = listings.flatMap((product) => {
      const distributionInfo = product.distributionInformation;
      const investmentDocuments = distributionInfo?.investmentDocuments;
      const platformName = product.distributionPlatformPlatformName;
      const platformId = product.id;

      if (!investmentDocuments || investmentDocuments.length === 0) {
        return [];
      }

      return investmentDocuments.map((doc) => ({
        ...doc,
        distributionPlatformName: platformName,
        distributionPlatformId: platformId,
      }));
    });
    setInvestmentDocuments(docs);

    // get platforms with edit permission
    setListingsWithEditPermission(
      listings.filter((listing) =>
        relevantDistributionPlatforms.some(
          (platform) =>
            platform.id === listing.distributionPlatformId &&
            listing.distributionInformation?.investmentDocuments.length === 0 &&
            platform.permissions.includes(
              DistributionPlatformPermissionEnum.EDIT_DISTRIBUTION_INFORMATION_AND_INVESTMENT_DOCUMENTS,
            ),
        ),
      ),
    );
  }, [JSON.stringify(listings), JSON.stringify(currentUser?.associatedDistributionPlatforms)]);

  if (!hasViewDistributionInformationPermission) return null;

  return (
    <WideContent
      title={<Translate name="investmentDocuments.title" />}
      ignorePageTitle
      titleSpacing="small"
      headerPanel={
        <AddInvestmentDocuments
          distributionPlatforms={listingsWithEditPermission}
          onInvestmentDocumentsAdded={onInvestmentDocumentsAdded}
        />
      }
    >
      <InvestmentDocumentsList
        documents={investmentDocuments}
        onInvestmentDocumentsEdited={onInvestmentDocumentsAdded}
      />
    </WideContent>
  );
};

export default InvestmentDocuments;
